import { computed, inject, Injectable } from '@angular/core';
import { StoreService } from '@webapp/core/services/store.service';
import { SessionsService, IUserSession } from '@webapp/auth';
import { firstValueFrom } from 'rxjs';
import { IUserDetail } from '../interfaces/users.interface';
import { UsersService } from '../services/users.service';

export interface MyProfileState {
	profile: IUserDetail | null;
	profileLoading: boolean;
	sessions: IUserSession[];
	sessionsLoading: boolean;
	sessionsError: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class MyProfileStore extends StoreService<MyProfileState> {
	private usersService = inject(UsersService);
	private sessionsService = inject(SessionsService);

	constructor() {
		super();
		this.setState({
			profile: null,
			profileLoading: false,
			sessions: [],
			sessionsLoading: false,
			sessionsError: false,
		});
	}

	currentSession = computed(() => this.select('sessions')().find(s => s.current));
	otherSessions = computed(() => this.select('sessions')().filter(s => !s.current));
	canRevokeOthers = computed(() => this.select('sessions')().some(s => s.current));

	async loadProfile() {
		this.set('profileLoading', true);

		try {
			const profile = await firstValueFrom(this.usersService.getMe());
			this.set('profile', profile);
		} catch (error) {
			console.error('Error loading profile:', error);
		} finally {
			this.set('profileLoading', false);
		}
	}

	async loadSessions() {
		this.set('sessionsLoading', true);

		try {
			const sessions = await firstValueFrom(this.sessionsService.getSessions());
			this.set('sessions', sessions);
			this.set('sessionsError', false);
		} catch (error) {
			console.error('Error loading sessions:', error);
			this.set('sessionsError', true);
		} finally {
			this.set('sessionsLoading', false);
		}
	}

	async revokeSession(sid: string) {
		await firstValueFrom(this.sessionsService.revokeSession(sid));
		await this.loadSessions();
	}

	async revokeOthers() {
		await firstValueFrom(this.sessionsService.revokeOthers());
		await this.loadSessions();
	}
}
